import { FC, useMemo, useEffect, useReducer, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import { Box } from '@mui/material';

import DocViewer, {
  DocRenderer,
  DocRendererProps,
  IConfig,
  ITheme,
  IDocument,
  DocViewerRenderers,
} from '@cyntler/react-doc-viewer';

// Helper function to wrap renderers with callback support
const wrapRendererWithCallbacks = (renderer: DocRenderer): DocRenderer => {
  const WrappedRenderer: FC<DocRendererProps> = (props) => {
    const { mainState } = props;
    const onRenderSuccess = (mainState.config as IEnhancedConfig)
      ?.onRenderSuccess;
    const firedRef = useRef(false);

    useEffect(() => {
      firedRef.current = false;
    }, [mainState.currentDocument]);

    useEffect(() => {
      if (firedRef.current) return;
      // Call onRenderSuccess after a short delay to allow rendering to complete
      const timer = setTimeout(() => {
        if (!firedRef.current) {
          firedRef.current = true;
          onRenderSuccess?.();
        }
      }, 500);

      return () => clearTimeout(timer);
    }, [onRenderSuccess, mainState.currentDocument]);

    // Render the original component
    const OriginalRenderer = renderer as FC<DocRendererProps>;
    return <OriginalRenderer {...props} />;
  };

  const wrapped = WrappedRenderer as DocRenderer;
  wrapped.fileTypes = renderer.fileTypes;
  wrapped.weight = renderer.weight;
  return wrapped;
};

// Extended config interface to include PDF state

interface IEnhancedConfig extends IConfig {
  pdfState?: IPDFState;

  dispatch?: React.Dispatch<PDFActions>;

  onRenderSuccess?: () => void;

  onRenderError?: (error: Error) => void;
}

// PDF State Types and Actions (matching cyntler/react-doc-viewer)

const SET_ZOOM_LEVEL = 'SET_ZOOM_LEVEL';

const SET_PDF_PAGINATED = 'SET_PDF_PAGINATED';

const SET_NUM_PAGES = 'SET_NUM_PAGES';

const SET_CURRENT_PAGE = 'SET_CURRENT_PAGE';

interface SetZoomLevel {
  type: typeof SET_ZOOM_LEVEL;

  value: number;
}

interface SetPDFPaginated {
  type: typeof SET_PDF_PAGINATED;

  value: boolean;
}

interface SetNumPages {
  type: typeof SET_NUM_PAGES;

  value: number;
}

interface SetCurrentPage {
  type: typeof SET_CURRENT_PAGE;

  value: number;
}

export type PDFActions =
  | SetZoomLevel
  | SetPDFPaginated
  | SetNumPages
  | SetCurrentPage;

export interface IPDFState {
  defaultZoomLevel: number;

  zoomLevel: number;

  zoomJump: number;

  paginated: boolean;

  numPages: number;

  currentPage: number;
}

export const initialPDFState: IPDFState = {
  defaultZoomLevel: 1,

  zoomLevel: 1,

  zoomJump: 0.1,

  paginated: true,

  numPages: 0,

  currentPage: 1,
};

export const setZoomLevel = (value: number): SetZoomLevel => ({
  type: SET_ZOOM_LEVEL,

  value,
});

export const setPDFPaginated = (value: boolean): SetPDFPaginated => ({
  type: SET_PDF_PAGINATED,

  value,
});

export const setNumPages = (value: number): SetNumPages => ({
  type: SET_NUM_PAGES,

  value,
});

export const setCurrentPage = (value: number): SetCurrentPage => ({
  type: SET_CURRENT_PAGE,

  value,
});

export const pdfReducer = (state: IPDFState, action: PDFActions): IPDFState => {
  switch (action.type) {
    case SET_ZOOM_LEVEL:
      return { ...state, zoomLevel: action.value };

    case SET_PDF_PAGINATED:
      return { ...state, paginated: action.value };

    case SET_NUM_PAGES:
      return { ...state, numPages: action.value };

    case SET_CURRENT_PAGE:
      return { ...state, currentPage: action.value };

    default:
      return state;
  }
};

interface EnhancedDocViewerProps {
  documents: IDocument[];

  className?: string;

  style?: React.CSSProperties;

  config?: IConfig;

  theme?: ITheme;

  pluginRenderers?: DocRenderer[];

  prefetchMethod?: string;

  requestHeaders?: Record<string, string>;

  initialActiveDocument?: IDocument;

  language?:
    | 'pt'
    | 'pl'
    | 'fr'
    | 'en'
    | 'es'
    | 'de'
    | 'it'
    | 'ar'
    | 'sr'
    | 'sr_cyr'
    | 'ja'
    | 'ru'
    | 'se';

  activeDocument?: IDocument;

  onDocumentChange?: (document: IDocument) => void;

  pdfState?: IPDFState;

  dispatch?: React.Dispatch<PDFActions>;

  onRenderSuccess?: () => void;

  onRenderError?: (error: Error) => void;
}

// Custom PDF Renderer with full PDF.js configuration

const CustomPDFRenderer: FC<DocRendererProps> = ({ mainState }) => {
  const { currentDocument } = mainState;

  // Extract pdfState and dispatch from config if provided

  const externalPdfState = (mainState.config as IEnhancedConfig)?.pdfState;

  const externalDispatch = (mainState.config as IEnhancedConfig)?.dispatch;

  const onRenderSuccess = (mainState.config as IEnhancedConfig)
    ?.onRenderSuccess;

  const onRenderError = (mainState.config as IEnhancedConfig)?.onRenderError;

  // Use external pdfState/dispatch if provided, otherwise use internal reducer

  const internalPdfState = useReducer(pdfReducer, {
    ...initialPDFState,

    defaultZoomLevel:
      mainState.config?.pdfZoom?.defaultZoom ??
      initialPDFState.defaultZoomLevel,

    zoomLevel:
      mainState.config?.pdfZoom?.defaultZoom ?? initialPDFState.zoomLevel,

    zoomJump: mainState.config?.pdfZoom?.zoomJump ?? initialPDFState.zoomJump,

    paginated: mainState.config?.pdfVerticalScrollByDefault
      ? false
      : initialPDFState.paginated,
  });

  const pdfState = externalPdfState || internalPdfState[0];

  const dispatch = externalDispatch || internalPdfState[1];

  // Reset pagination when document changes

  useEffect(() => {
    if (currentDocument) {
      dispatch(setNumPages(0));

      dispatch(setCurrentPage(1));
    }
  }, [currentDocument, dispatch]);

  // Move all hooks to the top (Rules of Hooks)

  const pdfOptions = useMemo(
    () => ({
      renderForms: true, // Enable interactive form field rendering (checkboxes, etc.)
    }),
    [],
  );

  const isPdfFile = useMemo(() => {
    if (!currentDocument) return false;

    const fileType = currentDocument.fileType?.toLowerCase();

    const fileName = currentDocument.fileName?.toLowerCase();

    const uri = currentDocument.uri?.toLowerCase();

    return (
      fileType === 'pdf' ||
      fileType === 'application/pdf' ||
      fileType === 'application/octet-stream' ||
      fileName?.endsWith('.pdf') ||
      uri?.startsWith('blob:') // Assume blob URLs are PDFs for now
    );
  }, [currentDocument]);

  const onDocumentLoadSuccess = ({
    numPages: totalPages,
  }: {
    numPages: number;
  }) => {
    dispatch(setNumPages(totalPages));

    onRenderSuccess?.();
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    onRenderError?.(error);
  };

  if (!currentDocument || !isPdfFile) {
    return (
      <Box
        sx={{
          display: 'flex',

          justifyContent: 'center',

          alignItems: 'center',

          height: '100%',

          color: '#fff',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ fontSize: '18px', marginBottom: 2 }}>
            Unsupported File Type
          </Box>

          <Box sx={{ fontSize: '14px', opacity: 0.7 }}>
            This file type is not supported for preview
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className="custom-pdf-renderer"
      sx={{
        width: '100%',

        height: '100%',

        display: 'flex',

        flexDirection: 'column',
      }}
    >
      {/* Scrollable content area */}

      <Box
        sx={{
          flex: 1,

          overflow: 'auto',

          display: 'flex',

          justifyContent: 'center',

          alignItems: 'flex-start',
        }}
      >
        <Box>
          <Document
            file={currentDocument.uri}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            options={pdfOptions}
          >
            {pdfState.paginated ? (
              <Page
                pageNumber={pdfState.currentPage}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                scale={pdfState.zoomLevel}
                width={800}
              />
            ) : (
              // Render all pages when not paginated

              Array.from({ length: pdfState.numPages }, (_, index) => (
                <Page
                  key={`page-${index + 1}`}
                  pageNumber={index + 1}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  scale={pdfState.zoomLevel}
                  width={800}
                />
              ))
            )}
          </Document>
        </Box>
      </Box>
    </Box>
  );
};

// Create the renderer object

const customPDFRenderer = CustomPDFRenderer as DocRenderer;

customPDFRenderer.fileTypes = ['pdf', 'application/octet-stream']; // Handle octet-stream for PDFs

customPDFRenderer.weight = 100;

const TXT_FILE_TYPES = ['txt', 'text/plain'];

const EnhancedDocViewer: FC<EnhancedDocViewerProps> = ({
  pluginRenderers,

  config,

  style,

  pdfState,

  dispatch,

  onRenderSuccess,

  onRenderError,

  ...docViewerProps
}) => {
  // Fire onRenderSuccess for TXT files since their renderer is not wrapped
  const activeDocument =
    docViewerProps.activeDocument ?? docViewerProps.documents?.[0];
  const onRenderSuccessRef = useRef(onRenderSuccess);
  onRenderSuccessRef.current = onRenderSuccess;

  useEffect(() => {
    if (!activeDocument) return;
    const fileType = activeDocument.fileType?.toLowerCase() ?? '';
    const fileName = activeDocument.fileName?.toLowerCase() ?? '';
    const isTxt =
      TXT_FILE_TYPES.includes(fileType) || fileName.endsWith('.txt');
    if (!isTxt) return;
    const timer = setTimeout(() => {
      onRenderSuccessRef.current?.();
    }, 500);
    return () => clearTimeout(timer);
  }, [activeDocument]);

  // Create renderers array with our custom PDF renderer
  const renderers = useMemo(() => {
    const defaultRenderers = DocViewerRenderers;

    const customRenderers = pluginRenderers || [];

    // Filter out default PDF renderer and use our custom one
    const filteredDefaultRenderers = defaultRenderers.filter(
      (renderer) => !renderer.fileTypes?.includes('pdf'),
    );

    // Filter out any PDF renderers from custom renderers too
    const filteredCustomRenderers = customRenderers.filter(
      (renderer) => !renderer.fileTypes?.includes('pdf'),
    );

    // Only wrap renderers that are safe to wrap (exclude txt/plain-text renderers
    // which rely on internal fetch/URI handling that breaks when wrapped)
    const shouldWrap = (renderer: DocRenderer) =>
      !renderer.fileTypes?.some((ft) =>
        ['txt', 'text/plain'].includes(ft.toLowerCase()),
      );

    // Wrap non-PDF, non-TXT renderers with callback support
    const wrappedDefaultRenderers = filteredDefaultRenderers.map((renderer) =>
      shouldWrap(renderer) ? wrapRendererWithCallbacks(renderer) : renderer,
    );

    const wrappedCustomRenderers = filteredCustomRenderers.map((renderer) =>
      shouldWrap(renderer) ? wrapRendererWithCallbacks(renderer) : renderer,
    );

    return [
      customPDFRenderer,

      ...wrappedDefaultRenderers,

      ...wrappedCustomRenderers,
    ];
  }, [pluginRenderers]);

  // Pass pdfState and dispatch to DocViewer via config

  const enhancedConfig = useMemo(
    () => ({
      ...config,

      pdfState,

      dispatch,

      onRenderSuccess,

      onRenderError,
    }),

    [config, pdfState, dispatch, onRenderSuccess, onRenderError],
  );

  return (
    <DocViewer
      {...docViewerProps}
      config={enhancedConfig}
      pluginRenderers={renderers}
      style={style}
    />
  );
};

export default EnhancedDocViewer;
